import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import {
  GUEST_TOKEN_KEY,
  GUEST_ID_KEY,
  GUEST_NAME_KEY,
  GUEST_SEEN_KEY,
} from '@/lib/guestStorage';
import type { CreateGuestInput } from '@/lib/api';

interface GuestState {
  guestId: string | null;
  guestName: string | null;
  creditsRemaining: number;
  isGuest: boolean;
  isLoaded: boolean;
}

interface GuestContextType extends GuestState {
  startGuestSession: (input: CreateGuestInput) => Promise<void>;
  exitGuestSession: () => void;
  refreshGuestCredits: () => Promise<void>;
  getGuestToken: () => string | null;
}

const GuestContext = createContext<GuestContextType | null>(null);

export { GUEST_TOKEN_KEY, GUEST_ID_KEY, GUEST_NAME_KEY, GUEST_SEEN_KEY };

export function GuestProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const [state, setState] = useState<GuestState>({
    guestId: null,
    guestName: null,
    creditsRemaining: 0,
    isGuest: false,
    isLoaded: false,
  });

  const getGuestToken = useCallback(() => {
    return localStorage.getItem(GUEST_TOKEN_KEY);
  }, []);

  const exitGuestSession = useCallback(() => {
    localStorage.removeItem(GUEST_TOKEN_KEY);
    localStorage.removeItem(GUEST_ID_KEY);
    localStorage.removeItem(GUEST_NAME_KEY);
    api.setGuestToken(null);
    setState({ guestId: null, guestName: null, creditsRemaining: 0, isGuest: false, isLoaded: true });
  }, []);

  // A guest session and a signed-in session are mutually exclusive: signing in or
  // registering ends the guest session. Without this a visitor who registered after
  // running out of free messages stayed `isGuest` with 0 guest credits, and the chat
  // page kept showing them the "create a free account" credits popup they had just
  // acted on. This also runs on mount, so a stale guest token from a previous visit
  // is discarded rather than restored alongside the user's own session.
  useEffect(() => {
    if (!authLoaded) return;

    if (isSignedIn) {
      exitGuestSession();
      return;
    }

    // Signing in mid-restore must win over an in-flight getGuestMe.
    let cancelled = false;

    const token = localStorage.getItem(GUEST_TOKEN_KEY);
    const guestId = localStorage.getItem(GUEST_ID_KEY);
    // guestName stored in localStorage, accessed via state

    if (token && guestId) {
      api.setGuestToken(token);
      api.getGuestMe().then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          setState({
            guestId: res.data.guestId,
            guestName: res.data.name,
            creditsRemaining: res.data.creditsRemaining,
            isGuest: true,
            isLoaded: true,
          });
        } else {
          localStorage.removeItem(GUEST_TOKEN_KEY);
          localStorage.removeItem(GUEST_ID_KEY);
          localStorage.removeItem(GUEST_NAME_KEY);
          api.setGuestToken(null);
          setState({ guestId: null, guestName: null, creditsRemaining: 0, isGuest: false, isLoaded: true });
        }
      }).catch(() => {
        if (cancelled) return;
        setState({ guestId: null, guestName: null, creditsRemaining: 0, isGuest: false, isLoaded: true });
      });
    } else {
      setState((s) => ({ ...s, isLoaded: true }));
    }

    return () => { cancelled = true; };
    // exitGuestSession is stable (it only touches localStorage and setState).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoaded, isSignedIn]);

  const startGuestSession = async (input: CreateGuestInput) => {
    const res = await api.createGuestSession(input);
    if (!res.success || !res.data) throw new Error(res.error?.message || 'Failed to create guest session');

    const { guestId, token, creditsRemaining, name } = res.data;
    localStorage.setItem(GUEST_TOKEN_KEY, token);
    localStorage.setItem(GUEST_ID_KEY, guestId);
    localStorage.setItem(GUEST_NAME_KEY, name);
    localStorage.setItem(GUEST_SEEN_KEY, 'true');
    api.setGuestToken(token);

    setState({ guestId, guestName: name, creditsRemaining, isGuest: true, isLoaded: true });
  };

  const refreshGuestCredits = async () => {
    const res = await api.getGuestMe();
    if (res.success && res.data) {
      setState((s) => ({ ...s, creditsRemaining: res.data!.creditsRemaining }));
    }
  };

  return (
    <GuestContext.Provider value={{ ...state, startGuestSession, exitGuestSession, refreshGuestCredits, getGuestToken }}>
      {children}
    </GuestContext.Provider>
  );
}

export function useGuest() {
  const context = useContext(GuestContext);
  if (!context) throw new Error('useGuest must be used within GuestProvider');
  return context;
}
